// src/pages/admin/AdminAboutSettings.jsx
import React, { useState, useEffect } from 'react';

export default function AdminAboutSettings() {
    const [videoUrl, setVideoUrl] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(''); // State mới để lưu link preview local
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    useEffect(() => {
        fetchCurrentVideo();
    }, []);

    // Cleanup bộ nhớ: Xóa link preview cũ khi component bị hủy để tránh rò rỉ bộ nhớ
    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const fetchCurrentVideo = async () => {
        try {
            const token = localStorage.getItem('skillmatch_token');
            const res = await fetch(`${API_URL}/api/admin/settings/about-video`, {
                method: 'GET',
                headers: {
                    'x-auth-token': token 
                }
            }); 
            
            if (!res.ok) throw new Error("Không thể lấy dữ liệu video");
            
            const data = await res.json();
            setVideoUrl(data.videoUrl || '');
        } catch (error) {
            console.error("Lỗi lấy video:", error);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 50 * 1024 * 1024) {
                setMessage({ type: 'error', text: 'Dung lượng file quá lớn. Vui lòng chọn video dưới 50MB.' });
                e.target.value = null;
                return;
            }
            setSelectedFile(file);
            // Tạo link xem trước ngay lập tức từ file vừa chọn
            setPreviewUrl(URL.createObjectURL(file)); 
            setMessage({ type: '', text: '' });
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            setMessage({ type: 'error', text: 'Vui lòng chọn một file video trước khi lưu!' });
            return;
        }

        setIsLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const token = localStorage.getItem('skillmatch_token');
            const formData = new FormData();
            formData.append('video', selectedFile);

            const res = await fetch(`${API_URL}/api/admin/settings/upload-about-video`, {
                method: 'POST',
                headers: {
                    'x-auth-token': token 
                },
                body: formData
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || 'Có lỗi xảy ra khi tải lên.');
            }
            
            setVideoUrl(data.videoUrl);
            setMessage({ type: 'success', text: 'Tải lên video thành công!' });
            
            // Reset các state sau khi upload thành công
            setSelectedFile(null); 
            setPreviewUrl(''); 
            document.getElementById('file-upload').value = ''; 
            
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if(!window.confirm("Bạn có chắc chắn muốn xóa video này?")) return;
        try {
            setIsLoading(true);
            const token = localStorage.getItem('skillmatch_token');
            
            const res = await fetch(`${API_URL}/api/admin/settings/about-video`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'x-auth-token': token 
                },
                body: JSON.stringify({ videoUrl: '' })
            });
            
            if (!res.ok) {
                throw new Error('Lỗi khi xóa video');
            }

            setVideoUrl('');
            setPreviewUrl('');
            setSelectedFile(null);
            document.getElementById('file-upload').value = ''; 
            setMessage({ type: 'success', text: 'Đã gỡ video!' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Lỗi khi xóa video.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Xác định video nào sẽ được hiển thị: Ưu tiên video đang chọn (preview), nếu không có thì hiện video trên server
    const displayVideo = previewUrl || videoUrl;

    return (
        <div className="bg-white p-8 rounded-xl shadow-sm max-w-2xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Cài đặt trang About</h2>
            
            {message.text && (
                <div className={`p-4 mb-6 rounded ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {message.text}
                </div>
            )}

            <form onSubmit={handleUpload} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload Video Giới thiệu (.mp4, .mov)
                    </label>
                    <div className="flex gap-2 items-center">
                        <input
                            id="file-upload"
                            type="file"
                            accept="video/mp4,video/x-m4v,video/*"
                            onChange={handleFileChange}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {videoUrl && !previewUrl && (
                            <button 
                                type="button"
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition"
                            >
                                Xóa Video Server
                            </button>
                        )}
                        {previewUrl && (
                            <button 
                                type="button"
                                onClick={() => {
                                    setPreviewUrl('');
                                    setSelectedFile(null);
                                    document.getElementById('file-upload').value = '';
                                }}
                                className="px-4 py-2 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition"
                            >
                                Hủy chọn
                            </button>
                        )}
                    </div>
                </div>

                {displayVideo && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <p className={`text-sm font-medium mb-2 ${previewUrl ? 'text-blue-600' : 'text-gray-500'}`}>
                            {previewUrl ? '👀 Video đang chọn (Chưa lưu):' : '✅ Video hiện tại trên hệ thống:'}
                        </p>
                        <div className="bg-black rounded overflow-hidden flex justify-center">
                            {/* Thuộc tính key giúp React load lại player khi đổi từ videoUrl sang previewUrl */}
                            <video key={displayVideo} width="100%" controls style={{ maxHeight: '350px' }}>
                                <source src={displayVideo} type="video/mp4" />
                                Trình duyệt không hỗ trợ thẻ video.
                            </video>
                        </div>
                    </div>
                )}

                <div className="flex justify-end pt-4 border-t border-gray-100">
                    <button
                        type="submit"
                        disabled={isLoading || !selectedFile}
                        className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isLoading ? 'Đang tải lên Cloudinary...' : 'Lưu và Tải lên'}
                    </button>
                </div>
            </form>
        </div>
    );
}